/**
 * Helper functions for normalizing different data formats for the MultiSelectWidget
 */

/**
 * Normalizes assessment data to common format
 * @param {Array} assessments - Array of assessment objects
 * @returns {Array} Normalized array with common structure
 */
export const normalizeAssessmentData = (assessments) => {
  if (!Array.isArray(assessments)) {
    console.warn('normalizeAssessmentData: Expected array, got:', typeof assessments);
    return [];
  }
  
  return assessments.map(assessment => {
    if (!assessment || typeof assessment !== 'object') {
      console.warn('normalizeAssessmentData: Invalid assessment object:', assessment);
      return null;
    }
    
    return {
      id: assessment.id,
      key: assessment.id,
      title: assessment.title || 'Untitled Assessment',
      description: assessment.description || '',
      categories: assessment.categories || [],
      hasAutomation: Boolean(assessment.hasAutomation),
      type: 'assessment'
    };
  }).filter(Boolean);
};

/**
 * Normalizes resource data to common format
 * @param {Array} resources - Array of resource objects
 * @returns {Array} Normalized array with common structure
 */
export const normalizeResourceData = (resources) => {
  if (!Array.isArray(resources)) {
    console.warn('normalizeResourceData: Expected array, got:', typeof resources);
    return [];
  }
  
  return resources.map(resource => {
    if (!resource || typeof resource !== 'object') {
      console.warn('normalizeResourceData: Invalid resource object:', resource);
      return null;
    }
    
    return {
      id: resource.resourceKey,
      key: resource.resourceKey,
      title: resource.title || 'Untitled Resource',
      description: resource.description || '',
      type: resource.type || 'resource',
      hasAutomation: false // Resources don't have automation
    };
  }).filter(Boolean);
};

/**
 * Normalizes selected assessment data to common format
 * @param {Array} selectedItems - Array of selected items with enableAutomation
 * @returns {Array} Normalized array with common structure
 */
export const normalizeSelectedAssessmentData = (selectedItems) => {
  if (!Array.isArray(selectedItems)) {
    console.warn('normalizeSelectedAssessmentData: Expected array, got:', typeof selectedItems);
    return [];
  }
  
  return selectedItems.map(item => {
    if (!item || typeof item !== 'object') {
      console.warn('normalizeSelectedAssessmentData: Invalid item:', item);
      return null;
    }
    
    return {
      key: item.key,
      enableAutomation: Boolean(item.enableAutomation)
    };
  }).filter(Boolean);
};

/**
 * Normalizes selected resource data to common format (simple array of keys)
 * @param {Array} selectedKeys - Array of selected resource keys
 * @returns {Array} Normalized array with common structure
 */
export const normalizeSelectedResourceData = (selectedKeys) => {
  if (!Array.isArray(selectedKeys)) {
    console.warn('normalizeSelectedResourceData: Expected array, got:', typeof selectedKeys);
    return [];
  }
  
  return selectedKeys.map(key => {
    if (typeof key !== 'string') {
      console.warn('normalizeSelectedResourceData: Invalid key:', key);
      return null;
    }
    
    return {
      key: key,
      enableAutomation: false // Resources don't have automation
    };
  }).filter(Boolean);
};

/**
 * Converts normalized selected data back to assessment format
 * @param {Array} normalizedData - Normalized selected data
 * @returns {Array} Assessment format with enableAutomation
 */
export const denormalizeToAssessmentFormat = (normalizedData) => {
  if (!Array.isArray(normalizedData)) {
    console.warn('denormalizeToAssessmentFormat: Expected array, got:', typeof normalizedData);
    return [];
  }
  
  return normalizedData.map(item => {
    if (!item || typeof item !== 'object') {
      console.warn('denormalizeToAssessmentFormat: Invalid item:', item);
      return null;
    }
    
    return {
      key: item.key,
      enableAutomation: Boolean(item.enableAutomation)
    };
  }).filter(Boolean);
};

/**
 * Converts normalized selected data back to resource format (simple key array)
 * @param {Array} normalizedData - Normalized selected data
 * @returns {Array} Simple array of keys
 */
export const denormalizeToResourceFormat = (normalizedData) => {
  if (!Array.isArray(normalizedData)) {
    console.warn('denormalizeToResourceFormat: Expected array, got:', typeof normalizedData);
    return [];
  }
  
  return normalizedData.map(item => {
    if (!item || typeof item !== 'object') {
      console.warn('denormalizeToResourceFormat: Invalid item:', item);
      return null;
    }
    
    return item.key;
  }).filter(Boolean);
};